/**
 * Custom hook for handling click outside behavior
 * Uses backdrop approach instead of direct DOM manipulation
 */

import { useCallback } from 'react';

export interface UseClickOutsideOptions {
	enabled?: boolean;
}

/**
 * Hook that provides backdrop approach for click outside behavior
 * @param callback - Function to call when clicking outside
 * @param options - Configuration options
 * @returns backdrop props and handlers for implementing click outside
 */
export const useClickOutside = (
	callback: () => void,
	options: UseClickOutsideOptions = {},
) => {
	const { enabled = true } = options;

	const handleBackdropClick = useCallback(() => {
		if (!enabled) return;

		// Always trigger callback when backdrop is clicked
		callback();
	}, [callback, enabled]);

	return {
		// Props for the backdrop element
		backdropProps: {
			onClick: handleBackdropClick,
			style: {
				position: 'fixed' as const,
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 1000,
				backgroundColor: 'transparent',
			},
		},
		// No content props needed - let content handle its own events naturally
		contentProps: {},
	};
};
